import { Component, OnInit } from '@angular/core';
import { WhatsAppReportService } from '../services/whatsapp-report.service';
import { LoginService } from '../login.service';
import { INgxMyDpOptions, IMyDateModel } from 'ngx-mydatepicker';

@Component({
  selector: 'app-whatsapp-campaign-report',
  templateUrl: './whatsapp-campaign-report.component.html',
  styleUrls: ['./whatsapp-campaign-report.component.css']
})
export class WhatsappCampaignReportComponent implements OnInit {
  campaigns: any[] = [];
  loading = false;
  startDatePicker: any;
  endDatePicker: any;
  myOptions: INgxMyDpOptions = {
    dateFormat: 'dd/mm/yyyy',
  };

  filters = {
    start_date: '',
    end_date: '',
    campaign_id: '',
    status: ''
  };

  statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'sent', label: 'Sent' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'read', label: 'Read' },
    { value: 'failed', label: 'Failed' }
  ];

  currentUser: any;
  showDetailsModal = false;
  selectedCampaign: any = null;
  campaignDetails: any[] = [];

  constructor(
    private reportService: WhatsAppReportService,
    private loginservice: LoginService
  ) { }

  ngOnInit() {
    this.currentUser = JSON.parse(this.loginservice.getLocalSession('currentUser'));
    this.setDefaultDates();
    this.loadCampaigns();
  }

  setDefaultDates() {
    const today = new Date();
    const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    this.startDatePicker = this.formatDateForPicker(lastMonth);
    this.endDatePicker = this.formatDateForPicker(today);

    this.filters.start_date = this.formatYMD(this.startDatePicker);
    this.filters.end_date = this.formatYMD(this.endDatePicker);
  }

  onStartDateChanged(event: IMyDateModel): void {
    this.startDatePicker = event;
    this.filters.start_date = this.formatYMD(event);
    this.onFilterChange();
  }

  onEndDateChanged(event: IMyDateModel): void {
    this.endDatePicker = event;
    this.filters.end_date = this.formatYMD(event);
    this.onFilterChange();
  }

  formatDateForPicker(dateObj: Date) {
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();

    return {
      date: { year: year, month: month, day: day },
      jsdate: dateObj.toISOString(),
      formatted: this.formatDDMMYYYY(day, month, year),
      epoc: Math.floor(dateObj.getTime() / 1000)
    };
  }

  formatDDMMYYYY(day: number, month: number, year: number): string {
    return this.pad(day) + '/' + this.pad(month) + '/' + year;
  }

  pad(n: number): string {
    return n < 10 ? '0' + n : n.toString();
  }

  formatYMD(obj: any) {
    if (!obj || !obj.date) return '';
    const d = obj.date;
    return `${d.year}-${('0' + d.month).slice(-2)}-${('0' + d.day).slice(-2)}`;
  }

  loadCampaigns() {
    this.loading = true;
    this.reportService.getCampaignReports(this.filters, this.currentUser.salon_id).subscribe(
      response => {
        this.campaigns = response.data || [];
        this.loading = false;
      },
      error => {
        console.error('Error loading campaigns:', error);
        this.loading = false;
      }
    );
  }

  onFilterChange() {
    this.loadCampaigns();
  }

  clearFilters() {
    this.filters = {
      start_date: '',
      end_date: '',
      campaign_id: '',
      status: ''
    };
    this.setDefaultDates();
    this.loadCampaigns();
  }

  exportToCSV() {
    this.loading = true;
    const promises = this.campaigns.map(campaign => 
      this.reportService.getSentMessageReports({ campaign_id: campaign.campaign_id }, this.currentUser.salon_id).toPromise()
    );

    Promise.all(promises).then(responses => {
      const allData: any[] = [];
      
      this.campaigns.forEach((campaign, index) => {
        const details = responses[index].data || [];
        details.forEach((detail: any) => {
          allData.push({
            campaign_name: campaign.campaign_name,
            template_name: campaign.template_name,
            total_messages: campaign.total_messages,
            delivered_count: campaign.delivered_count,
            read_count: campaign.read_count,
            failed_count: campaign.failed_count,
            delivery_rate: campaign.delivery_rate,
            read_rate: campaign.read_rate,
            created_at: campaign.created_at,
            campaign_status: campaign.campaign_status,
            recipient_wa_id: detail.recipient_wa_id,
            customer_name: detail.customer_name,
            messageContent: detail.messageContent,
            current_status: detail.current_status,
            sent_at: detail.sent_at,
            delivered_at: detail.delivered_at,
            read_at: detail.read_at,
            failure_reason: detail.failure_reason
          });
        });
      });

      const csvData = this.convertToCSV(allData);
      this.downloadCSV(csvData, 'whatsapp-campaign-report-detailed.csv');
      this.loading = false;
    }).catch(error => {
      console.error('Error exporting:', error);
      this.loading = false;
    });
  }

  private convertToCSV(data: any[]): string {
    const headers = ['Campaign Name', 'Template', 'Total Sent', 'Delivered', 'Read', 'Failed', 'Delivery Rate', 'Read Rate', 'Created At', 'Campaign Status', 'Phone Number', 'Customer Name', 'Message', 'Status', 'Sent At', 'Delivered At', 'Read At'];
    const csvArray = [headers.join(',')];

    data.forEach(row => {
      const csvRow = [
        row.campaign_name,
        row.template_name,
        row.total_messages,
        row.delivered_count,
        row.read_count,
        row.failed_count,
        row.delivery_rate + '%',
        row.read_rate + '%',
        new Date(row.created_at).toLocaleString('en-GB'),
        row.campaign_status,
        row.recipient_wa_id,
        row.customer_name || 'N/A',
        '"' + (row.messageContent || '').replace(/"/g, '""') + '"',
        row.current_status,
        this.formatDateTime(row.sent_at),
        this.formatDateTime(row.delivered_at),
        this.formatDateTime(row.read_at)
      ];
      csvArray.push(csvRow.join(','));
    });

    return csvArray.join('\n');
  }

  private downloadCSV(csvData: string, filename: string) {
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  viewCampaignDetails(campaign: any) {
    this.selectedCampaign = campaign;
    this.loading = true;
    this.reportService.getSentMessageReports({ campaign_id: campaign.campaign_id }, this.currentUser.salon_id).subscribe(
      response => {
        this.campaignDetails = response.data || [];
        this.showDetailsModal = true;
        this.loading = false;
      },
      error => {
        console.error('Error loading campaign details:', error);
        this.loading = false;
      }
    );
  }

  closeDetailsModal() {
    this.showDetailsModal = false;
    this.selectedCampaign = null;
    this.campaignDetails = [];
  }

  formatDateTime(timestamp: string): string {
    if (!timestamp || timestamp === '0000-00-00 00:00:00') return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
}



// WEBPACK FOOTER //
// ./src/app/whatsapp-campaign-report/whatsapp-campaign-report.component.ts